import { CheckIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import { CreateGuard } from '@/components/guards/permission-guards';
import { Button } from '@/components/ui/button';
import FilterSelect from '@/components/ui/filter-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MultiFilterSelect from '@/components/ui/multi-filter-select';
import SearchableSelect, {
  type SearchableSelectOption,
} from '@/components/ui/searchable-select';
import { Textarea } from '@/components/ui/textarea';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useToast } from '@/contexts/ToastContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { ACTIONS, RESOURCES } from '@/utils/permissions';

// Types
interface RequisitionItem {
  inventoryId: string;
  variantId?: string;
  requestedQuantity: number;
  reason: string;
}

interface RequisitionFormData {
  items: RequisitionItem[];
  priority: 'low' | 'medium' | 'high' | 'urgent';
  notes?: string;
  regionId?: string;
  sourcingOfficers?: string[];
  dateRequired?: string;
}

interface InventoryItem {
  _id: string;
  name: string;
  measurementUnit: string;
  quantity: number;
  reorderLevel: number;
  isVariant: boolean;
  variant?: Array<{
    _id: string;
    name: string;
    quantity: number;
    price: number;
  }>;
}

interface Region {
  _id: string;
  regionName: string;
  sourcingOfficers: Array<{
    _id: {
      _id: string;
      username: string;
    };
    username: string;
  }>;
}

interface RequisitionCreateSheetProps {
  onRequisitionCreated?: () => Promise<void>;
}

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const totalSteps = 2;
const stepLabels = ['Requisition Information', 'Products'];

// Step indicator component
const StepIndicator: React.FC<{
  currentStep: number;
  totalSteps: number;
  stepLabels: string[];
}> = ({ currentStep, totalSteps, stepLabels }) => {
  return (
    <div className='flex items-center justify-between mb-8'>
      {Array.from({ length: totalSteps }, (_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;
        const isLast = stepNumber === totalSteps;

        return (
          <React.Fragment key={stepNumber}>
            <div className='flex flex-col items-center'>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isCompleted
                    ? 'bg-success-60 text-white'
                    : isActive
                      ? 'bg-success-60 text-white'
                      : 'bg-gray-20 text-gray-60'
                }`}
              >
                {isCompleted ? <CheckIcon className='w-4 h-4' /> : stepNumber}
              </div>
              <span className='text-xs mt-2 text-gray-60 text-center'>
                {stepLabels[index]}
              </span>
            </div>
            {!isLast && (
              <div
                className={`flex-1 h-0.5 mx-4 ${
                  isCompleted || (isActive && stepNumber > 1)
                    ? 'bg-success-60'
                    : 'bg-gray-20'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const RequisitionCreateSheet: React.FC<RequisitionCreateSheetProps> = ({
  onRequisitionCreated,
}) => {
  const api = useEnhancedApi();
  const { get } = api;
  const { hideSheet } = useGlobalState();
  const { showSuccess, showError } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [inventoryById, setInventoryById] = useState<
    Record<string, InventoryItem>
  >({});
  const [regions, setRegions] = useState<Region[]>([]);
  const [officers, setOfficers] = useState<
    Array<{ _id: string; username: string }>
  >([]);

  const { control, handleSubmit, trigger, reset, watch, setValue } =
    useForm<RequisitionFormData>({
      defaultValues: {
        items: [
          {
            inventoryId: '',
            variantId: '',
            requestedQuantity: 1,
            reason: '',
          },
        ],
        priority: 'medium',
        notes: '',
        regionId: '',
        sourcingOfficers: [],
        dateRequired: '',
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedRegion = watch('regionId');

  // Fetch inventory items and regions on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [inventoryResponse, regionsResponse] = await Promise.all([
          api.inventory.getAll(),
          get('/region'),
        ]);

        if (inventoryResponse.success) {
          const list =
            inventoryResponse.data?.inventory?.docs ||
            inventoryResponse.data?.inventory ||
            inventoryResponse.data?.items ||
            inventoryResponse.data ||
            [];
          setInventoryItems(list);
          setInventoryById(
            (list as InventoryItem[]).reduce(
              (acc, it) => {
                acc[it._id] = it;
                return acc;
              },
              {} as Record<string, InventoryItem>
            )
          );
        }

        if (regionsResponse.success) {
          setRegions(regionsResponse.data?.regions || []);
        }

        // Fetch users that have READ on regions resource (sourcing officers)
        const usersWithPermission = await get('/roles/permissions/users', {
          resource: RESOURCES.REGIONS,
          action: ACTIONS.READ,
        });
        if (usersWithPermission.success) {
          const mapped = (usersWithPermission.data?.users || []).map(
            (u: {
              _id: string;
              username?: string;
              firstName?: string;
              email: string;
            }) => ({
              _id: u._id,
              username: u.username || u.firstName || u.email,
            })
          );
          setOfficers(mapped);
        }
      } catch {
        showError('Error loading data. Please try again.');
      }
    };

    fetchData();
  }, [get, api.inventory, showError]);

  // Reset sourcing officers when region changes
  useEffect(() => {
    if (watchedRegion) {
      setValue('sourcingOfficers', []);
    }
  }, [watchedRegion, setValue]);

  const onSubmit = useCallback(
    async (formData: RequisitionFormData) => {
      setIsLoading(true);
      try {
        // Validate that at least one item is provided and complete
        const validItems = formData.items.filter(item => {
          return item.inventoryId && item.requestedQuantity > 0 && item.reason;
        });

        if (validItems.length === 0) {
          showError(
            'Please add at least one valid item with all required fields'
          );
          setIsLoading(false);
          return;
        }

        const submitData = {
          ...formData,
          items: validItems,
        };

        const response = await api.requisitions.create(submitData);

        if (response.success) {
          showSuccess(
            response.message || 'Purchase requisition created successfully'
          );
          reset();
          setCurrentStep(1);
          hideSheet('create-requisition');

          // Refresh the requisitions list if callback provided
          if (onRequisitionCreated) {
            await onRequisitionCreated();
          }
        } else {
          showError(
            response.error?.message || 'Failed to create purchase requisition'
          );
        }
      } catch {
        showError('An error occurred while creating the purchase requisition');
      } finally {
        setIsLoading(false);
      }
    },
    [
      api.requisitions,
      showError,
      showSuccess,
      reset,
      hideSheet,
      onRequisitionCreated,
    ]
  );

  // Handle step navigation
  const handleNextStep = useCallback(async () => {
    let isValid = true;

    if (currentStep === 1) {
      // Validate first step fields
      isValid = await trigger([
        'regionId',
        'sourcingOfficers',
        'priority',
        'dateRequired',
      ]);
    } else if (currentStep === 2) {
      // Validate that we have at least one item
      const currentItems = watch('items') || [];
      const validItems = currentItems.filter(
        item => item.inventoryId && item.requestedQuantity > 0 && item.reason
      );
      if (validItems.length === 0) {
        showError('At least one complete product item is required');
        return;
      }
    }

    if (isValid && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else if (isValid && currentStep === totalSteps) {
      // Final step - submit form
      await handleSubmit(onSubmit)();
    }
  }, [currentStep, trigger, watch, showError, handleSubmit, onSubmit]);

  const handlePrevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleCancel = useCallback(() => {
    reset();
    setCurrentStep(1);
    hideSheet('create-requisition');
  }, [reset, hideSheet]);

  // Expose handlers for dynamic button system
  useEffect(() => {
    const handlers = {
      handleNextStep,
      handlePrevStep,
      handleCancel,
      isLoading,
      currentStep,
      totalSteps,
    };

    // Store handlers globally
    (
      window as unknown as { requisitionCreateHandlers?: typeof handlers }
    ).requisitionCreateHandlers = handlers;

    // Cleanup on unmount
    return () => {
      const windowObj = window as unknown as {
        requisitionCreateHandlers?: typeof handlers;
      };
      if (windowObj.requisitionCreateHandlers) {
        delete windowObj.requisitionCreateHandlers;
      }
    };
  }, [handleNextStep, handlePrevStep, handleCancel, isLoading, currentStep]);

  // Item management functions
  const addNewItem = useCallback(() => {
    append({
      inventoryId: '',
      variantId: '',
      requestedQuantity: 1,
      reason: '',
    });
  }, [append]);

  const removeItem = useCallback(
    (index: number) => {
      if (fields.length > 1) {
        remove(index);
      }
    },
    [fields.length, remove]
  );

  // Options for inventory dropdown
  const inventoryOptions = inventoryItems.map(item => ({
    value: item._id,
    label: `${item.name} (Available: ${item.quantity} ${item.measurementUnit})`,
  }));

  const searchInventory = useCallback(
    async (query: string): Promise<SearchableSelectOption[]> => {
      const res = await api.inventory.getAll({ search: query, limit: 20 });
      if (!res.success) return [];
      const list: InventoryItem[] =
        res.data?.inventory?.docs ||
        res.data?.inventory ||
        res.data?.items ||
        res.data ||
        [];
      // update cache
      setInventoryById(prev => {
        const updated = { ...prev };
        list.forEach(it => {
          updated[it._id] = it;
        });
        return updated;
      });
      return list.map(it => ({
        value: it._id,
        label: `${it.name} (Available: ${it.quantity} ${it.measurementUnit})`,
      }));
    },
    [api.inventory]
  );

  // Options for regions dropdown
  const regionOptions = regions.map(region => ({
    value: region._id,
    label: region.regionName,
  }));

  // Options for sourcing officers dropdown: limit to users with READ on regions
  const sourcingOfficerOptions = officers.map(officer => ({
    value: officer._id,
    label: officer.username,
  }));

  return (
    <CreateGuard resource={RESOURCES.PURCHASE_REQUISITIONS}>
      <div className='space-y-6'>
        {/* Step indicator */}
        <StepIndicator
          currentStep={currentStep}
          totalSteps={totalSteps}
          stepLabels={stepLabels}
        />

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          {/* Step 1: Requisition Information */}
          {currentStep === 1 && (
            <div className='space-y-4'>
              {/* Sourcing Region */}
              <div className='space-y-2'>
                <Label htmlFor='regionId'>Sourcing Region</Label>
                <Controller
                  name='regionId'
                  control={control}
                  rules={{ required: 'Sourcing region is required' }}
                  render={({ field, fieldState }) => (
                    <FilterSelect
                      name='regionId'
                      value={field.value}
                      options={regionOptions}
                      placeholder='Select Sourcing region'
                      error={fieldState?.error?.message}
                      onChange={field.onChange}
                      className='w-full'
                    />
                  )}
                />
              </div>

              {/* Sourcing Officers */}
              <div className='space-y-2'>
                <Label htmlFor='sourcingOfficers'>
                  Select Sourcing Officers
                </Label>
                <Controller
                  name='sourcingOfficers'
                  control={control}
                  rules={{
                    required: 'At least one sourcing officer is required',
                  }}
                  render={({ field, fieldState }) => (
                    <MultiFilterSelect
                      name='sourcingOfficers'
                      value={field.value}
                      options={sourcingOfficerOptions}
                      placeholder='Select Select Sourcing Officers'
                      error={fieldState?.error?.message}
                      onChange={field.onChange}
                      className='w-full'
                    />
                  )}
                />
              </div>

              {/* Urgency Level */}
              <div className='space-y-2'>
                <Label htmlFor='priority'>Urgency Level</Label>
                <Controller
                  name='priority'
                  control={control}
                  rules={{ required: 'Priority is required' }}
                  render={({ field, fieldState }) => (
                    <FilterSelect
                      name='priority'
                      value={field.value}
                      options={priorityOptions}
                      placeholder='Select Urgency Level'
                      error={fieldState?.error?.message}
                      onChange={field.onChange}
                      className='w-full'
                    />
                  )}
                />
              </div>

              {/* Date Required */}
              <div className='space-y-2'>
                <Label htmlFor='dateRequired'>Date Required</Label>
                <Controller
                  name='dateRequired'
                  control={control}
                  rules={{ required: 'Date required is required' }}
                  render={({ field, fieldState }) => (
                    <Input
                      type='date'
                      value={field.value}
                      onChange={field.onChange}
                      placeholder='Select date required'
                      error={fieldState?.error?.message}
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* Step 2: Products */}
          {currentStep === 2 && (
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <Label>Requested Items</Label>
                <Button
                  type='button'
                  onClick={addNewItem}
                  className='flex items-center gap-2 text-brand-600 hover:text-brand-700 text-sm font-medium'
                  hierarchy='link'
                  size='sm'
                >
                  <PlusIcon className='h-4 w-4' />
                  Add New Item
                </Button>
              </div>

              {fields.map((field, index) => {
                const currentItem = watch(`items.${index}`);
                const selectedInventory =
                  inventoryItems.find(
                    inv => inv._id === currentItem?.inventoryId
                  ) || inventoryById[currentItem?.inventoryId];

                // Get variant options for selected inventory
                const variantOptions = selectedInventory?.isVariant
                  ? (selectedInventory.variant || []).map(variant => ({
                      value: variant._id,
                      label: `${variant.name} (Available: ${variant.quantity})`,
                    }))
                  : [];

                return (
                  <div
                    key={field.id}
                    className='border rounded-lg p-4 space-y-3'
                  >
                    <div className='flex items-center justify-between'>
                      <h4 className='text-sm font-medium text-gray-90'>
                        Item {index + 1}
                      </h4>
                      {fields.length > 1 && (
                        <Button
                          type='button'
                          onClick={() => removeItem(index)}
                          className='text-destructive-50 hover:text-destructive-70'
                          hierarchy='link'
                          size='sm'
                        >
                          <TrashIcon className='h-4 w-4' />
                        </Button>
                      )}
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                      {/* Item Selection */}
                      <div className='space-y-2'>
                        <Label>Item</Label>
                        <Controller
                          name={`items.${index}.inventoryId`}
                          control={control}
                          rules={{ required: 'Item selection is required' }}
                          render={({ field: itemField, fieldState }) => (
                            <SearchableSelect
                              name={`items.${index}.inventoryId`}
                              value={itemField.value}
                              options={inventoryOptions}
                              placeholder='Search or select item'
                              onSearch={searchInventory}
                              valueLabel={
                                itemField.value
                                  ? inventoryById[itemField.value]?.name ||
                                    inventoryItems.find(
                                      inv => inv._id === itemField.value
                                    )?.name
                                  : undefined
                              }
                              error={fieldState?.error?.message}
                              onChange={value => {
                                itemField.onChange(value);
                                // Reset variant when item changes
                                setValue(`items.${index}.variantId`, '');
                              }}
                              className='w-full'
                            />
                          )}
                        />
                      </div>

                      {/* Variant Selection (only show if item has variants) */}
                      {selectedInventory?.isVariant && (
                        <div className='space-y-2'>
                          <Label>Variant</Label>
                          <Controller
                            name={`items.${index}.variantId`}
                            control={control}
                            render={({ field: variantField, fieldState }) => (
                              <SearchableSelect
                                name={`items.${index}.variantId`}
                                value={variantField.value}
                                options={variantOptions}
                                placeholder='Search or select variant'
                                error={fieldState?.error?.message}
                                onChange={variantField.onChange}
                                className='w-full'
                              />
                            )}
                          />
                        </div>
                      )}

                      {/* Quantity */}
                      <div className='space-y-2'>
                        <Label>Quantity</Label>
                        <Controller
                          name={`items.${index}.requestedQuantity`}
                          control={control}
                          rules={{
                            required: 'Quantity is required',
                            min: {
                              value: 1,
                              message: 'Quantity must be at least 1',
                            },
                          }}
                          render={({ field: quantityField, fieldState }) => (
                            <Input
                              type='number'
                              min='1'
                              value={quantityField.value}
                              onChange={e =>
                                quantityField.onChange(
                                  Number(e.target.value) || 1
                                )
                              }
                              placeholder='Enter quantity'
                              error={fieldState?.error?.message}
                            />
                          )}
                        />
                        {selectedInventory && !selectedInventory.isVariant && (
                          <p className='text-xs text-gray-50'>
                            Current Stock: {selectedInventory.quantity}{' '}
                            {selectedInventory.measurementUnit}
                            {selectedInventory.quantity <=
                              selectedInventory.reorderLevel && (
                              <span className='text-destructive-50'>
                                {' '}
                                (Below reorder level)
                              </span>
                            )}
                          </p>
                        )}
                      </div>

                      {/* Reason */}
                      <div className='col-span-full space-y-2'>
                        <Label>Reason for Request</Label>
                        <Controller
                          name={`items.${index}.reason`}
                          control={control}
                          rules={{ required: 'Reason is required' }}
                          render={({ field: reasonField, fieldState }) => (
                            <Input
                              value={reasonField.value}
                              onChange={reasonField.onChange}
                              placeholder='e.g., Low stock, Upcoming demand'
                              error={fieldState?.error?.message}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Additional Notes */}
              <div className='space-y-2'>
                <Label htmlFor='notes'>Additional Notes (Optional)</Label>
                <Controller
                  name='notes'
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      placeholder='Any additional information about this requisition'
                      rows={4}
                    />
                  )}
                />
              </div>

              {/* Requisition Summary */}
              <div className='bg-gray-5 p-4 rounded-lg'>
                <h4 className='text-sm font-medium text-gray-90 mb-3'>
                  Requisition Summary
                </h4>
                <div className='space-y-3'>
                  <div className='flex justify-between text-sm'>
                    <span>Total Items:</span>
                    <span>
                      {
                        fields.filter((_, index) => {
                          const item = watch(`items.${index}`);
                          return item?.inventoryId;
                        }).length
                      }
                    </span>
                  </div>

                  <div className='space-y-2'>
                    <span className='text-sm font-medium text-gray-70'>
                      Items Requested:
                    </span>
                    {fields
                      .filter((_, index) => {
                        const item = watch(`items.${index}`);
                        return item?.inventoryId;
                      })
                      .map((_, index) => {
                        const item = watch(`items.${index}`);
                        const inventory =
                          inventoryItems.find(
                            inv => inv._id === item?.inventoryId
                          ) || inventoryById[item?.inventoryId];
                        const variant = inventory?.variant?.find(
                          v => v._id === item?.variantId
                        );
                        return (
                          <div
                            key={index}
                            className='text-xs text-gray-60 bg-white p-2 rounded border'
                          >
                            <div className='font-medium'>
                              {inventory?.name}
                              {variant && ` - ${variant.name}`}
                            </div>
                            <div>
                              Quantity: {item?.requestedQuantity}{' '}
                              {inventory?.measurementUnit}
                            </div>
                            <div>Reason: {item?.reason}</div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer buttons - REMOVED - Now handled by GlobalSheetRenderer */}
        </form>
      </div>
    </CreateGuard>
  );
};

export default RequisitionCreateSheet;
